import { useEffect, useRef, useState } from 'react';
import { BackBar, Card, PrimaryBtn, AIOutput, WhatsAppBtn, useCoachStream } from './healthShared';

const BOX_PHASES = [
  { label: 'Breathe in', secs: 4, scale: 1.25 },
  { label: 'Hold', secs: 4, scale: 1.25 },
  { label: 'Breathe out', secs: 4, scale: 0.85 },
  { label: 'Hold', secs: 4, scale: 0.85 },
];

function BreathingReset({ minutes, title, testId }) {
  const [active, setActive] = useState(false);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [remaining, setRemaining] = useState(minutes * 60);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!active) return undefined;
    let phaseTime = 0;
    timerRef.current = setInterval(() => {
      phaseTime += 1;
      setRemaining((r) => {
        if (r <= 1) { setActive(false); return minutes * 60; }
        return r - 1;
      });
      setPhaseIdx((i) => {
        if (phaseTime % BOX_PHASES[i].secs === 0) { phaseTime = 0; return (i + 1) % BOX_PHASES.length; }
        return i;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [active, minutes]);

  const phase = BOX_PHASES[phaseIdx];
  const mm = String(Math.floor(remaining / 60)).padStart(1, '0');
  const ss = String(remaining % 60).padStart(2, '0');

  return (
    <Card className="p-5 text-center" testId={testId}>
      <p className="text-xs font-black tracking-widest uppercase text-[#7FA183]">{title}</p>
      <div className="flex items-center justify-center my-5" style={{ height: 120 }}>
        <div
          className="w-24 h-24 rounded-full bg-[#2E4A3B]/10 border-2 border-[#7FA183] flex items-center justify-center transition-transform ease-in-out"
          style={{ transform: `scale(${active ? phase.scale : 1})`, transitionDuration: '3500ms' }}
        >
          <span className="text-xs font-bold text-[#2E4A3B]">{active ? phase.label : 'Ready'}</span>
        </div>
      </div>
      <p className="font-accent text-2xl text-[#273127] mb-3">{mm}:{ss}</p>
      <PrimaryBtn onClick={() => { setActive(!active); if (!active) { setRemaining(minutes * 60); setPhaseIdx(0); } }} testId={`${testId}-toggle`} className="px-8">
        {active ? 'Stop' : 'Start'}
      </PrimaryBtn>
    </Card>
  );
}

export default function SleepRecover({ profile, snapshot, onBack }) {
  const { output, running, run } = useCoachStream();

  return (
    <div data-testid="sleep-recover-section">
      <BackBar title="Sleep & Recover" onBack={onBack} />

      <p className="text-xs font-black tracking-widest uppercase text-[#7FA183] mb-3">Sleep</p>
      <Card className="p-5 mb-4" testId="sleep-stats-card">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-[10px] font-black tracking-widest uppercase text-[#9AA594]">Your sleep</p>
            <p className="font-accent text-2xl text-[#273127]">{profile?.sleep_hours ? `${profile.sleep_hours} hrs` : '—'}</p>
            <p className="text-[11px] text-[#9AA594]">target {snapshot?.sleep_target || '7–8 hrs'}</p>
          </div>
          <div>
            <p className="text-[10px] font-black tracking-widest uppercase text-[#9AA594]">Caffeine cut-off</p>
            <p className="font-accent text-2xl text-[#273127]">2 pm</p>
            <p className="text-[11px] text-[#9AA594]">for better sleep tonight</p>
          </div>
        </div>
        <ul className="mt-4 pt-4 border-t border-[#F0F2E8] space-y-2 text-sm text-[#33402F]">
          <li>🕙 <strong>Consistency beats duration</strong> — same bedtime daily, even weekends</li>
          <li>🌙 <strong>Night duty?</strong> Dark room + eye mask after shift; 20-min nap pre-shift</li>
          <li>📵 Screens off 30 minutes before bed</li>
        </ul>
        <button
          onClick={() => run('How can I improve my sleep given my schedule?', 'sleep')}
          data-testid="sleep-ai-btn"
          className="mt-4 text-xs font-bold text-[#2E4A3B] underline underline-offset-2"
        >
          {running ? 'Getting your sleep plan…' : 'Get my personalised sleep plan →'}
        </button>
        {output && (
          <div className="mt-3 pt-3 border-t border-[#F0F2E8]">
            <AIOutput text={output} testId="sleep-ai-output" />
            {!running && <div className="mt-3"><WhatsAppBtn text={output} testId="sleep-whatsapp-btn" /></div>}
          </div>
        )}
      </Card>

      <p className="text-xs font-black tracking-widest uppercase text-[#D97B5A] mb-3">Reset</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <BreathingReset minutes={2} title="2-minute breathing" testId="reset-breathing-2" />
        <BreathingReset minutes={5} title="5-minute relaxation" testId="reset-breathing-5" />
      </div>
      <Card className="p-5 mt-3" testId="stress-reset-card">
        <p className="text-xs font-black tracking-widest uppercase text-[#7FA183] mb-2">30-second stress resets</p>
        <ul className="space-y-2 text-sm text-[#33402F]">
          <li>🖐️ Press palms together hard for 10 seconds, release slowly</li>
          <li>🪟 Look out a window at something far away for 20 seconds</li>
          <li>💧 Splash cold water on your wrists between patients</li>
          <li>🧍 Stand, roll shoulders back 5 times, one deep breath</li>
        </ul>
        <p className="text-[11px] text-[#9AA594] mt-3">For ongoing stress or mood concerns, <a href="/gia" className="underline font-semibold text-[#2E4A3B]">Talk to Gia</a> or reach out to a professional.</p>
      </Card>
    </div>
  );
}
